import { PlusIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { unwrapConnection } from "@utils/utils";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  ApiReportTemplateFileUploadConfigChoices,
  ApiReportTreatmentPlanAiStgPredictionStatusChoices,
  ReportGoalType,
  ReportGoalTypeEdge,
  useCreateUpdateReportGoalMutation,
} from "@api/graphql/types-and-hooks";
import { AddShortTermGoalsBank } from "@components/GoalsBank/AddShortTermGoalsBank/AddShortTermGoalsBankV2";
import { MenuItem } from "@components/Menu";
import { notifyError } from "@components/notifications/notifications";
import { GroupedDomainRef } from "@pages/ClinicalReportDetails/ReportPage/utils/sortGoalsByDomain";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReportProgrammingData } from "@providers/ReportProgrammingProvider";
import { useReport } from "@providers/ReportProvider";

interface DomainActionsMenuProps {
  close: () => void;
  domain: GroupedDomainRef | null | undefined;
}

export const DomainActionsMenu: React.FC<DomainActionsMenuProps> = ({ close, domain }) => {
  const go = useNavigate();
  const { isReassessment } = useClinicalReportDetailsData();
  const { report, reportQuery } = useReport();

  const isCatalightTemplate =
    report?.reportTemplate?.fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight.valueOf();
  const reportTreatmentPlan = report?.reportTreatmentPlan;
  const aiStgPredictionStatus = reportTreatmentPlan?.aiStgPredictionStatus;

  const isAiGoalsReady =
    aiStgPredictionStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionPredicted;
  const isAiGoalsFailed = aiStgPredictionStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.Failure;
  const isAiGoalsLoading =
    aiStgPredictionStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.Pending ||
    aiStgPredictionStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionPending;

  const { dispatchSelected, reassessmentGoalList, reassessmentGoalsQuery } = useReportProgrammingData();
  const queryClient = useQueryClient();
  const { mutateAsync: createReportGoal } = useCreateUpdateReportGoalMutation({});

  const handleSuggestedGoalAdded = async () => {
    close();
    const previousGoalIds = new Set(reassessmentGoalList.map((g) => g.id));
    if (isReassessment) {
      const { data } = await reassessmentGoalsQuery.refetch();
      const newGoalList = (unwrapConnection(data?.getReassessmentReportGoals) as ReportGoalType[]) || [];
      const newlyAdded = newGoalList.find((g) => !previousGoalIds.has(g.id));
      if (newlyAdded) {
        dispatchSelected({ type: "SELECT_GOAL", goalId: newlyAdded.id });
      }
    }
    await reportQuery?.refetch?.();
    // Prefix-invalidate so other goal-keyed consumers (e.g. the header
    // Report Progress chip) refetch — `reassessmentGoalsQuery.refetch()`
    // above only re-runs its own query instance.
    queryClient.invalidateQueries({ queryKey: ["get-reassessment-goals"] });
  };

  const handleAddCustomGoal = async (domain: GroupedDomainRef | null) => {
    try {
      const response = await createReportGoal({
        goalInput: {
          reportId: report?.id ?? "<missing-report-id>",
          reportDomainId: domain?.id ?? undefined,
        },
        learnerId: report?.learner?.id ?? "<missing-learner-id>",
      });
      if (!response.createUpdateReportGoal?.status) {
        notifyError(response.createUpdateReportGoal?.message || "Failed to create custom goal");
        return;
      }
      // The backend seeds template default field values (incl. the name) on
      // create — no fields are sent here, matching the IA flow. Refetch so the
      // new goal shows in the sidebar, then open its editor.
      await reassessmentGoalsQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ["get-reassessment-goals"] });
      const createdId = response.createUpdateReportGoal?.reportGoalData?.id;
      if (createdId) {
        dispatchSelected({ type: "SELECT_GOAL", goalId: createdId });
      }
    } catch (err) {
      console.error("Error creating custom goal", err);
      notifyError("Failed to create custom goal");
    }
  };

  const handleManageEhrGoals = () => {
    go("../select-programming", { state: { returnToReportPage: true, domainId: domain?.id } });
  };
  const goalList: ReportGoalType[] = reassessmentGoalList;

  // Goals already assigned to the report (used to hide them from AI suggestions)
  const assignedGoalIds = useMemo(() => goalList.map((goal) => goal.id), [goalList]);
  const assignedGoalBankGoalIds = useMemo(
    () => goalList.filter((goal) => goal.goalBank?.id != null).map((goal) => goal.goalBank!.id),
    [goalList],
  );

  // AI-suggested goals for the selected domain, excluding already-assigned goals
  const aiGoalList = useMemo(() => {
    if (!isAiGoalsReady || !domain?.id) {
      return [];
    }
    const edges = reportTreatmentPlan?.reportgoalSet?.edges ?? [];
    const assignedSet = new Set(assignedGoalIds);
    return edges.filter(
      (edge): edge is ReportGoalTypeEdge =>
        !!edge?.node?.aiGenerated && edge.node.reportDomain?.id === domain?.id && !assignedSet.has(edge.node.id),
    );
  }, [isAiGoalsReady, domain, reportTreatmentPlan?.reportgoalSet?.edges, assignedGoalIds]);

  const sortedAiGoals = useMemo(() => [...aiGoalList].sort((_, gb) => (gb?.node?.inUse ? 1 : -1)), [aiGoalList]);

  return (
    <>
      <MenuItem
        onClick={() => {
          void handleAddCustomGoal(domain ?? null);
          close();
        }}
      >
        <PlusIcon className="w-4 h-4 mr-3 text-limestone-500 shrink-0" strokeWidth={2} />
        Custom Goal
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleManageEhrGoals();
          close();
        }}
      >
        <PlusIcon className="w-4 h-4 mr-3 text-limestone-500 shrink-0" strokeWidth={2} />
        Manage EHR goals
      </MenuItem>
      <AddShortTermGoalsBank
        modalButton={
          <MenuItem>
            <SparklesIcon className="w-4 h-4 mr-3 text-limestone-500 shrink-0" />
            Suggested Goal
          </MenuItem>
        }
        learnerId={report?.learner?.id ?? "<missing-learner-id>"}
        reportDomainId={domain?.id ?? undefined}
        reportId={report?.id ?? "<missing-assessment-id>"}
        onAdd={handleSuggestedGoalAdded}
        aiGoals={sortedAiGoals}
        isAiGoalsLoading={isAiGoalsLoading}
        isAiGoalsFailed={isAiGoalsFailed}
        isCatalightTemplate={isCatalightTemplate}
        assignedGoalBankGoalIds={assignedGoalBankGoalIds}
      />
    </>
  );
};
